import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AdminUserListItem } from '@proref/types';
import { Icon } from '../shared/Icons';
import { Button } from '../shared/Button';
import { Spinner } from '../shared/Spinner';
import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogCancel,
} from '../ui/alert-dialog';
import { useRemoveUser } from '../../hooks/use-users';
import { notifyError, notifySuccess } from '../../lib/toast';

// Remove-confirm for one roster row, shared by the federation roster and the platform-wide
// Users page. An AlertDialog gate on a destructive, unrecoverable action: removing revokes the
// person's sign-in and cascades their attempts/discussions/etc. server-side, so it is not
// undoable from the UI.
export function RemoveUserDialog({ user }: { user: AdminUserListItem }) {
  const { t } = useTranslation();
  const remove = useRemoveUser();
  const [open, setOpen] = useState(false);

  async function confirm() {
    try {
      await remove.mutateAsync(user.id);
      notifySuccess(t('admin.users.removeDialog.removedToast', { name: user.full_name }));
      setOpen(false);
    } catch (err) {
      notifyError(err, t('admin.users.removeDialog.errorToast'));
    }
  }

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <Button variant="ghost" size="sm" icon aria-label={t('admin.users.removeDialog.removeAria', { name: user.full_name })}>
          <Icon.trash className="h-3.5 w-3.5" />
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('admin.users.removeDialog.confirmTitle', { name: user.full_name })}</AlertDialogTitle>
          <AlertDialogDescription>
            {t('admin.users.removeDialog.confirmBody')}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel type="button" disabled={remove.isPending}>
            {t('common.cancel')}
          </AlertDialogCancel>
          <Button variant="danger" onClick={confirm} disabled={remove.isPending}>
            {remove.isPending ? <Spinner className="h-4 w-4" /> : t('admin.users.removeDialog.removeButton')}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
